'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, type ReactNode } from 'react';

import { prefetchSocialAuthProviders } from '@eliadmin/core/src/components/auth/social-auth-providers-cache';
import Link from '@eliadmin/core/src/components/link';
import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

import { authPaths } from '../header-account';
import { MobileCategories, type MobileCategory } from './mobile-categories';
import { useMobileCategories } from './mobile-categories-state';

/** Figma badge (290:37740 „Frame 10“) shows „999“ as its widest state – as the header cart. */
const MAX_COUNT = 999;

/** „Môj účet“ is active on the account pages and, for a guest, on the sign-in pages. */
const accountPaths = () => [
  ROUTES.PROFILE(),
  ROUTES.CHANGE_PASSWORD(),
  ROUTES.ORDERS(),
  ROUTES.RETURN(),
  ...authPaths(),
];

const favoritePaths = () => [ROUTES.FAVORITES(), ROUTES.FAVORITE_PRODUCTS()];

/** Core routes fall back to the homepage when a page is not set up – that never counts as a match. */
function isOn(pathname: string, paths: string[]) {
  const home = ROUTES.HOME() ?? '/';
  return paths.some(path => !arePathsEqual(path, home) && arePathsEqual(path, pathname));
}

const TAB = cn(
  'flex w-full min-w-0 flex-col items-center gap-1 rounded pb-0.5 pt-1',
  'focus-visible:shadow-focus focus-visible:outline-none',
);

/**
 * Figma „TabBar-item“ (71.6 × 49): icon 24 and the label (12 px, line 15) 4 px under it; active
 * icon #127A26 and label Bold #0E6A20, otherwise both #5D655D Regular.
 */
function TabContent({
  icon,
  label,
  isActive,
  badge,
}: {
  icon: IconName;
  label: string;
  isActive: boolean;
  badge?: ReactNode;
}) {
  return (
    <>
      <span className={cn('relative flex', isActive ? 'text-[#127A26]' : 'text-[#5D655D]')}>
        <Icon name={icon} size={24} />
        {badge}
      </span>
      <span
        className={cn(
          'nt-body-xs max-w-full truncate',
          isActive ? 'font-bold text-[#0E6A20]' : 'font-normal text-[#5D655D]',
        )}
      >
        {label}
      </span>
    </>
  );
}

/**
 * The tab of the current page (`isCurrent`) is shown active unless the categories view is open – then
 * only „Kategórie“ is (Figma 290:37677); `aria-current` stays with the page.
 */
type TabProps = { isCurrent: boolean; isViewOpen: boolean };

/**
 * Link tab: `aria-current="page"` on its own page, `"true"` on another page of its section (the
 * orders under „Môj účet“).
 */
function TabLink({
  href,
  isCurrent,
  children,
  ...props
}: {
  href: string;
  isCurrent: boolean;
  children: ReactNode;
  onFocus?: () => void;
  onMouseEnter?: () => void;
  onTouchStart?: () => void;
}) {
  const pathname = useLocalePathname();
  const current = arePathsEqual(href, pathname) ? 'page' : 'true';

  return (
    <Link href={href} aria-current={isCurrent ? current : undefined} className={TAB} {...props}>
      {children}
    </Link>
  );
}

/**
 * Figma „Košík“ with the magenta badge (18 px high, radius 4, 2 px white ring outside, 18 px right
 * and 7 px above the icon's corner): the core cart query as the header cart.
 */
function CartTab({ isCurrent, isViewOpen }: TabProps) {
  const { t } = useTranslations();
  const { data: cart } = useQuery(useCartQueryOpts());
  const count = cart?.count ?? 0;
  const shown = count > MAX_COUNT ? `${MAX_COUNT}+` : `${count}`;

  return (
    <TabLink href={ROUTES.CART() ?? '#'} isCurrent={isCurrent}>
      <TabContent
        icon="bag"
        label={t('text343')}
        isActive={isCurrent && !isViewOpen}
        badge={
          count > 0 && (
            <span
              aria-hidden="true"
              className="nt-body-xs pointer-events-none absolute left-[18px] top-[-7px] flex h-[18px] min-w-[18px] items-center justify-center rounded bg-[#E5007E] px-1 font-normal text-white ring-2 ring-white"
            >
              {shown}
            </span>
          )
        }
      />
      {count > 0 && <span className="sr-only">({count})</span>}
    </TabLink>
  );
}

/** „Obľúbené“: the profile favourites when signed in, the guest favourites page otherwise. */
function FavoritesTab({ isCurrent, isViewOpen }: TabProps) {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);
  const href = (me ? ROUTES.FAVORITES() : ROUTES.FAVORITE_PRODUCTS()) ?? '#';

  return (
    <TabLink href={href} isCurrent={isCurrent}>
      <TabContent icon="heart" label={t('text516')} isActive={isCurrent && !isViewOpen} />
    </TabLink>
  );
}

function GuestAccountLink({ query, isCurrent, isViewOpen }: TabProps & { query: string }) {
  const { t } = useTranslations();
  const pathname = useLocalePathname();
  const locale = useLocaleStore(state => state.currentLocale);
  const current = query ? `${pathname}?${query}` : pathname;
  const returnUrl = authPaths().some(path => arePathsEqual(path, pathname)) ? undefined : current;
  const warmUp = () => void prefetchSocialAuthProviders(locale);

  return (
    <TabLink
      href={ROUTES.SIGN_IN(returnUrl)}
      isCurrent={isCurrent}
      onFocus={warmUp}
      onMouseEnter={warmUp}
      onTouchStart={warmUp}
    >
      <TabContent icon="user" label={t('text197')} isActive={isCurrent && !isViewOpen} />
    </TabLink>
  );
}

function GuestAccountLinkWithQuery(props: TabProps) {
  return <GuestAccountLink query={useSearchParams().toString()} {...props} />;
}

/**
 * „Môj účet“: the profile when signed in; a guest goes to the sign-in page that returns here
 * afterwards (`?returnUrl=`, as the header account button – the query is read under Suspense).
 */
function AccountTab(props: TabProps) {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);

  if (me) {
    return (
      <TabLink href={ROUTES.PROFILE() ?? '#'} isCurrent={props.isCurrent}>
        <TabContent
          icon="user"
          label={t('text197')}
          isActive={props.isCurrent && !props.isViewOpen}
        />
      </TabLink>
    );
  }

  return (
    <Suspense fallback={<GuestAccountLink query="" {...props} />}>
      <GuestAccountLinkWithQuery {...props} />
    </Suspense>
  );
}

/**
 * Figma „Mobile-navbar“ (290:37740 in 290:37677 / 263:26086), below 1024 px on every page with the
 * site header: fixed to the bottom, white, radius 18 on top, shadow 0 −4 70 / 12 %, five equal tabs
 * in 16 px gutters 8 px under its top edge, the safe area under them. Domov, Kategórie (opens the
 * categories view – the tab is active and `aria-expanded` while it is open), Košík, Obľúbené,
 * Môj účet; the tab of the current page is active. Without category roots there is no
 * „Kategórie“ tab. Navigation, and a tablet turned to ≥ 1024 px, close the view.
 */
export function MobileTabBar({
  categoriesLabel,
  roots,
}: {
  /** Page field „Hlavička - kategórie“ (the desktop button); core „Kategórie“ when empty. */
  categoriesLabel: string;
  roots: MobileCategory[];
}) {
  const { t } = useTranslations();
  const route = useMobileCategories();
  const { pathname } = route;
  const isOpen = route.isOpen && roots.length > 0;
  const label = categoriesLabel || t('text176');

  const isHome = arePathsEqual(ROUTES.HOME() ?? '/', pathname);

  return (
    <DialogPrimitive.Root modal={false} open={isOpen} onOpenChange={route.setOpen}>
      {roots.length > 0 && (
        <MobileCategories
          label={label}
          roots={roots}
          isOpen={isOpen}
          onClose={() => route.setOpen(false)}
        />
      )}
      <nav className="fixed inset-x-0 bottom-0 z-40 rounded-t-[18px] bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_70px_rgba(0,0,0,0.12)] lg:hidden">
        <ul role="list" className="grid auto-cols-fr grid-flow-col px-4 pt-2">
          <li className="flex min-w-0">
            <TabLink href={ROUTES.HOME() ?? '/'} isCurrent={isHome}>
              <TabContent icon="house-line" label={t('text160')} isActive={isHome && !isOpen} />
            </TabLink>
          </li>
          {roots.length > 0 && (
            <li className="flex min-w-0">
              <DialogPrimitive.Trigger className={TAB}>
                <TabContent icon="squares-four" label={label} isActive={isOpen} />
              </DialogPrimitive.Trigger>
            </li>
          )}
          <li className="flex min-w-0">
            <CartTab
              isCurrent={arePathsEqual(ROUTES.CART() ?? '#', pathname)}
              isViewOpen={isOpen}
            />
          </li>
          <li className="flex min-w-0">
            <FavoritesTab isCurrent={isOn(pathname, favoritePaths())} isViewOpen={isOpen} />
          </li>
          <li className="flex min-w-0">
            <AccountTab isCurrent={isOn(pathname, accountPaths())} isViewOpen={isOpen} />
          </li>
        </ul>
      </nav>
    </DialogPrimitive.Root>
  );
}
